import { Bitmap, gridSprite } from "./bitmap.js";
import { COLORS, type Color, type ColorKey } from "./palette.js";

/** Logical resolution (16:10). Galaxy Tab A9+ 1920×1200 is exactly ×10. */
export const SCENE_W = 192;
export const SCENE_H = 120;
export const FLOOR_Y = 94;
/** Default line the avatar's feet walk on. */
export const WALK_Y = 106;

export interface Anchor {
  x: number;
  y: number;
}

export const ANCHORS: Record<string, Anchor> = {
  door: { x: 19, y: 96 },
  door_out: { x: 19, y: 92 },
  training: { x: 48, y: WALK_Y },
  desk: { x: 87, y: 100 },
  window: { x: 87, y: 100 },
  center: { x: 96, y: WALK_Y },
  bed: { x: 128, y: 98 },
  bed_lie: { x: 131, y: 92 },
  shelf: { x: 151, y: WALK_Y },
  stove: { x: 158, y: WALK_Y },
  fridge: { x: 170, y: WALK_Y },
  console: { x: 92, y: 108 },
  bags: { x: 38, y: 100 },
};

type RectSpec = readonly [number, number, number, number, ColorKey];

function rects(w: number, h: number, specs: readonly RectSpec[]): Bitmap {
  const bm = new Bitmap(w, h, 0, 0);
  for (const [x, y, rw, rh, c] of specs) bm.rect(x, y, rw, rh, COLORS[c]);
  return bm;
}

/** Furniture and objects: sprite + top-left position in the room. */
export interface Placed {
  x: number;
  y: number;
  sprite: Bitmap;
  /** Drawn in front of the avatar. */
  front?: boolean;
}

const MATTRESS = rects(32, 7, [
  [0, 1, 32, 6, "ink"],
  [1, 0, 30, 1, "ink"],
  [1, 1, 30, 5, "cream"],
  [1, 5, 30, 1, "greyLight"],
  [2, 1, 7, 3, "paper"],
  [10, 2, 20, 1, "greyLight"],
]);

const BED = rects(36, 15, [
  [0, 0, 3, 15, "ink"],
  [1, 1, 1, 13, "woodLight"],
  [0, 6, 36, 7, "ink"],
  [1, 7, 34, 2, "paper"],
  [2, 4, 8, 4, "paper"],
  [10, 6, 25, 4, "blue"],
  [10, 9, 25, 1, "blueDark"],
  [1, 10, 34, 2, "woodLight"],
  [1, 12, 2, 3, "floorDark"],
  [33, 12, 2, 3, "floorDark"],
]);

const ITEM_ART: Record<string, Placed> = {
  mattress: { x: 112, y: 87, sprite: MATTRESS },
  bed: { x: 110, y: 79, sprite: BED },
  mat: {
    x: 34,
    y: 101,
    sprite: rects(28, 4, [
      [0, 0, 28, 4, "ink"],
      [1, 1, 26, 2, "green"],
      [1, 2, 26, 1, "greenDark"],
    ]),
  },
  pullup_bar: {
    x: 6,
    y: 41,
    sprite: rects(26, 2, [
      [0, 0, 26, 2, "ink"],
      [1, 0, 24, 1, "greyLight"],
    ]),
  },
  dumbbells: {
    x: 58,
    y: 90,
    sprite: gridSprite(["k.k.k.k", "kgk.kgk", "k.k.k.k", "......."], "top-left"),
  },
  bench: {
    x: 36,
    y: 84,
    sprite: rects(22, 10, [
      [0, 0, 22, 4, "ink"],
      [1, 1, 20, 2, "red"],
      [3, 4, 2, 6, "greyDark"],
      [17, 4, 2, 6, "greyDark"],
      [2, 9, 18, 1, "greyDark"],
    ]),
  },
  book_stack: {
    x: 147,
    y: 86,
    sprite: gridSprite(
      ["kkkkkkk", "krrrrrk", "kbbbbbk.", ".keeeeek", ".kyyyyyk", "kuuuuuk", "kkkkkkk"],
      "top-left",
    ),
  },
  bookshelf: {
    x: 145,
    y: 52,
    sprite: rects(14, 42, [
      [0, 0, 14, 42, "ink"],
      [1, 1, 12, 40, "floor"],
      [2, 2, 10, 9, "floorDark"],
      [2, 12, 10, 9, "floorDark"],
      [2, 22, 10, 9, "floorDark"],
      [2, 32, 10, 8, "floorDark"],
      [1, 11, 12, 1, "woodLight"],
      [1, 21, 12, 1, "woodLight"],
      [1, 31, 12, 1, "woodLight"],
    ]),
  },
  reading_lamp: {
    x: 105,
    y: 62,
    sprite: gridSprite(
      [
        ".kkkkk.",
        "kyyyyyk",
        "kyYYYyk",
        "kkkkkkk",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "...G...",
        "..kGk..",
        ".kkkkk.",
        ".kGGGk.",
        ".kkkkk.",
      ],
      "top-left",
    ),
  },
  armchair: {
    x: 150,
    y: 96,
    front: true,
    sprite: rects(18, 15, [
      [0, 0, 18, 15, "ink"],
      [2, 1, 14, 7, "purple"],
      [1, 5, 4, 8, "purple"],
      [13, 5, 4, 8, "purple"],
      [5, 8, 8, 4, "dusk"],
      [1, 13, 2, 2, "floorDark"],
      [15, 13, 2, 2, "floorDark"],
      [3, 2, 12, 1, "pink"],
    ]),
  },
  laptop: {
    x: 80,
    y: 89,
    sprite: rects(10, 5, [
      [0, 0, 10, 5, "ink"],
      [1, 1, 8, 3, "grey"],
      [1, 3, 8, 1, "greyDark"],
    ]),
  },
  desk: {
    x: 70,
    y: 72,
    sprite: rects(34, 22, [
      [0, 0, 34, 4, "ink"],
      [1, 1, 32, 2, "woodLight"],
      [2, 4, 3, 18, "ink"],
      [3, 4, 1, 18, "floor"],
      [29, 4, 3, 18, "ink"],
      [30, 4, 1, 18, "floor"],
      [22, 4, 7, 8, "ink"],
      [23, 4, 5, 7, "floor"],
      [25, 7, 1, 1, "yellow"],
    ]),
  },
  desk_laptop: {
    x: 82,
    y: 64,
    sprite: rects(11, 8, [
      [0, 0, 11, 8, "ink"],
      [1, 1, 9, 5, "skyDay"],
      [1, 6, 9, 1, "grey"],
      [0, 7, 11, 1, "greyDark"],
    ]),
  },
  chair: {
    x: 99,
    y: 77,
    sprite: rects(9, 17, [
      [0, 0, 9, 17, "ink"],
      [1, 1, 7, 7, "red"],
      [1, 8, 7, 2, "redDark"],
      [1, 10, 7, 7, "ink"],
      [3, 10, 3, 7, "greyDark"],
      [1, 15, 7, 2, "greyDark"],
    ]),
  },
  monitor2: {
    x: 71,
    y: 60,
    sprite: rects(10, 12, [
      [0, 0, 10, 9, "ink"],
      [1, 1, 8, 7, "teal"],
      [2, 2, 4, 1, "paper"],
      [2, 4, 5, 1, "paper"],
      [4, 9, 2, 2, "ink"],
      [2, 11, 6, 1, "ink"],
    ]),
  },
  trash_bin: {
    x: 159,
    y: 85,
    sprite: gridSprite(
      ["kkkkkkk", "kgggggk", ".kgagk.", ".kgagk.", ".kgagk.", ".kgagk.", ".kgagk.", ".kgggk.", ".kkkkk."],
      "top-left",
    ),
  },
  plant: {
    x: 69,
    y: 38,
    sprite: gridSprite(
      [
        "..e.e..",
        ".eEe.e.",
        "eEeEeEe",
        ".eEeEe.",
        "..eEe..",
        ".kkkkk.",
        ".kooook",
        ".kooook",
        "..kook.",
        "..kkkk.",
      ],
      "top-left",
    ),
  },
  rug: {
    x: 64,
    y: 104,
    sprite: rects(56, 10, [
      [0, 0, 56, 10, "ink"],
      [1, 1, 54, 8, "redDark"],
      [3, 2, 50, 6, "red"],
      [5, 4, 46, 2, "orange"],
      [8, 3, 2, 4, "yellow"],
      [46, 3, 2, 4, "yellow"],
    ]),
  },
  curtains: {
    x: 62,
    y: 10,
    sprite: rects(52, 46, [
      [0, 0, 52, 2, "ink"],
      [1, 0, 50, 1, "greyLight"],
      [1, 2, 7, 44, "ink"],
      [2, 2, 5, 43, "red"],
      [4, 2, 1, 43, "redDark"],
      [44, 2, 7, 44, "ink"],
      [45, 2, 5, 43, "red"],
      [47, 2, 1, 43, "redDark"],
    ]),
  },
  // Relacje milestones: photos with friends on the wall, a shelf with board games.
  photos: {
    x: 148,
    y: 14,
    sprite: rects(20, 20, [
      [0, 0, 9, 8, "ink"],
      [1, 1, 7, 6, "skyDay"],
      [2, 3, 2, 3, "skin1"],
      [5, 3, 2, 3, "skin3"],
      [1, 5, 7, 2, "green"],
      [11, 2, 8, 10, "ink"],
      [12, 3, 6, 8, "cream"],
      [13, 5, 2, 3, "skin2"],
      [15, 4, 2, 4, "skin0"],
      [12, 9, 6, 2, "pink"],
      [3, 11, 7, 7, "ink"],
      [4, 12, 5, 5, "yellowLight"],
      [5, 13, 3, 3, "orange"],
    ]),
  },
  board_games: {
    x: 145,
    y: 37,
    sprite: rects(18, 12, [
      [0, 9, 18, 2, "woodLight"],
      [0, 11, 18, 1, "ink"],
      [1, 6, 15, 3, "red"],
      [1, 6, 15, 1, "redDark"],
      [2, 3, 13, 3, "blue"],
      [2, 3, 13, 1, "blueDark"],
      [3, 0, 10, 3, "yellow"],
      [3, 0, 10, 1, "orange"],
      [6, 7, 4, 1, "paper"],
      [6, 4, 4, 1, "paper"],
    ]),
  },
  poster: {
    x: 123,
    y: 22,
    sprite: rects(14, 18, [
      [0, 0, 14, 18, "ink"],
      [1, 1, 12, 16, "dusk"],
      [3, 3, 8, 6, "orange"],
      [5, 4, 4, 4, "yellow"],
      [2, 11, 10, 1, "paper"],
      [3, 13, 8, 1, "paper"],
      [1, 15, 12, 2, "night"],
    ]),
  },
  radio: {
    x: 97,
    y: 44,
    sprite: gridSprite(
      ["....k...", "...k....", "kkkkkkkk", "kGGGGrrk", "kGkGGrrk", "kGGGGGGk", "kkkkkkkk"],
      "top-left",
    ),
  },
  vacuum: {
    x: 31,
    y: 78,
    sprite: gridSprite(
      [
        "kk...",
        "kgk..",
        ".kg..",
        ".kg..",
        ".kg..",
        ".kg..",
        ".kg..",
        ".kg..",
        "kkkkk",
        "krrrk",
        "krrrk",
        "krrrk",
        "kkkkk",
        "k...k",
        ".....",
      ],
      "top-left",
    ),
  },
  guitar: {
    x: 139,
    y: 26,
    sprite: gridSprite(
      [
        ".kk.",
        ".mk.",
        ".mk.",
        ".mk.",
        ".mk.",
        ".mk.",
        ".mk.",
        ".mk.",
        "kkkk.",
        "kookk",
        "kookk",
        ".kmk.",
        "kookk",
        "kooook",
        "kooook",
        ".kkkk.",
      ],
      "top-left",
    ),
  },
  dartboard: {
    x: 58,
    y: 50,
    sprite: gridSprite(
      [".kkkkk.", "kRhRhRk", "khrrrhk", "kRrkrRk", "khrrrhk", "kRhRhRk", ".kkkkk."],
      "top-left",
    ),
  },
  console: {
    x: 86,
    y: 110,
    front: true,
    sprite: rects(14, 5, [
      [0, 0, 14, 5, "ink"],
      [1, 1, 12, 3, "greyLight"],
      [2, 2, 3, 1, "teal"],
      [10, 2, 2, 1, "red"],
    ]),
  },
  tv: {
    x: 34,
    y: 24,
    sprite: rects(24, 17, [
      [0, 0, 24, 15, "ink"],
      [1, 1, 22, 13, "night"],
      [2, 2, 8, 3, "dusk"],
      [10, 15, 4, 2, "ink"],
    ]),
  },
};

/** Room position and sprite of an item (undefined for unknown ids). */
export function itemArt(id: string): Placed | undefined {
  return ITEM_ART[id];
}

export function itemCenterX(id: string): number {
  const a = ITEM_ART[id];
  return a ? Math.round(a.x + a.sprite.w / 2) : ANCHORS.center!.x;
}

export const ITEM_IDS = Object.keys(ITEM_ART);

export interface RoomView {
  /** Local time in minutes, drives the sky. */
  minutes: number;
  items: readonly string[];
  mess: number;
  doorOpen: boolean;
  bulbOn: boolean;
  /** Number of items on the shopping list (lines on the fridge note). */
  shopping: number;
  /** Mind level fills the bookshelf. */
  mindLevel: number;
  /** Deterministic per-day seed (stars). */
  seed: number;
  /** "jestem na: telefon" note on the door when the companion is elsewhere. */
  awayNote: boolean;
}

/** Sky bands for the window by local time. */
export function skyBands(minutes: number): Color[] {
  const h = minutes / 60;
  if (h < 5 || h >= 21) return [COLORS.night, COLORS.night, COLORS.dusk];
  if (h < 6.5) return [COLORS.dusk, COLORS.skyDusk, COLORS.orange];
  if (h < 8) return [COLORS.skyHigh, COLORS.skyDay, COLORS.skyDusk];
  if (h < 18) return [COLORS.skyHigh, COLORS.skyDay, COLORS.skyDay];
  if (h < 19.5) return [COLORS.skyHigh, COLORS.skyDusk, COLORS.orange];
  return [COLORS.dusk, COLORS.purple, COLORS.skyDusk];
}

/** Static room layer: walls, floor, window with sky, door, fridge, furniture, mess. */
export function composeRoom(v: RoomView): Bitmap {
  const bm = new Bitmap(SCENE_W, SCENE_H, 0, 0);
  // Wall.
  bm.rect(0, 0, SCENE_W, FLOOR_Y, COLORS.wall);
  bm.rect(0, 0, SCENE_W, 3, COLORS.wallDark);
  for (let i = 0; i < 9; i++) bm.set(((v.seed * 7 + i * 37) % 180) + 6, ((i * 23) % 70) + 8, COLORS.wallDark);
  bm.rect(0, FLOOR_Y - 3, SCENE_W, 3, COLORS.wallDark);
  bm.rect(0, FLOOR_Y - 1, SCENE_W, 1, COLORS.ink);
  // Floor planks.
  bm.rect(0, FLOOR_Y, SCENE_W, SCENE_H - FLOOR_Y, COLORS.floor);
  for (let y = FLOOR_Y + 5; y < SCENE_H; y += 6) bm.rect(0, y, SCENE_W, 1, COLORS.floorDark);
  for (let y = FLOOR_Y, row = 0; y < SCENE_H; y += 6, row++)
    for (let x = (row * 29) % 40; x < SCENE_W; x += 40) bm.rect(x, y, 1, 5, COLORS.floorDark);
  bm.rect(0, FLOOR_Y, SCENE_W, 1, COLORS.floorLight);

  drawWindow(bm, v);
  drawDoor(bm, v);
  drawFridge(bm, v);
  drawBulb(bm, v.bulbOn);

  const has = new Set(v.items);
  const draw = (id: string) => {
    const a = ITEM_ART[id];
    if (a && !a.front) bm.blit(a.sprite, a.x, a.y);
  };
  if (!has.has("bed")) draw("mattress");
  for (const id of v.items) {
    if (id === "laptop" && has.has("desk")) continue;
    draw(id);
  }
  if (has.has("desk") && has.has("laptop")) draw("desk_laptop");
  if (has.has("bookshelf")) drawBooks(bm, v.mindLevel);
  drawMess(bm, v.mess);
  return bm;
}

/** Items drawn after the avatar. */
export function frontItems(items: readonly string[]): Placed[] {
  return items.map((id) => ITEM_ART[id]).filter((a): a is Placed => Boolean(a?.front));
}

function drawWindow(bm: Bitmap, v: RoomView) {
  const x = 68;
  const y = 14;
  const w = 40;
  const h = 36;
  bm.rect(x - 1, y - 1, w + 2, h + 2, COLORS.ink);
  bm.rect(x, y, w, h, COLORS.woodLight);
  const ix = x + 2;
  const iy = y + 2;
  const iw = w - 4;
  const ih = h - 4;
  const bands = skyBands(v.minutes);
  const bandH = Math.ceil(ih / bands.length);
  bands.forEach((c, i) => bm.rect(ix, iy + i * bandH, iw, Math.min(bandH, ih - i * bandH), c));
  const hour = v.minutes / 60;
  const night = hour < 5 || hour >= 21;
  if (night) {
    for (let i = 0; i < 10; i++) {
      const sx = ix + ((v.seed * 13 + i * 11) % iw);
      const sy = iy + ((v.seed * 5 + i * 7) % (ih - 8));
      bm.set(sx, sy, i % 3 === 0 ? COLORS.yellowLight : COLORS.paper);
    }
    // Moon.
    bm.rect(ix + 24, iy + 4, 5, 5, COLORS.paper);
    bm.rect(ix + 26, iy + 4, 3, 3, bands[0]!);
  } else {
    // Sun travels left to right between 6:00 and 20:00.
    const t = Math.max(0, Math.min(1, (hour - 6) / 14));
    const sx = ix + 2 + Math.round(t * (iw - 8));
    const sy = iy + 2 + Math.round(Math.abs(t - 0.5) * 2 * (ih - 12));
    bm.rect(sx, sy, 4, 4, COLORS.yellow);
    bm.rect(sx + 1, sy + 1, 2, 2, COLORS.yellowLight);
    // A cloud.
    const cx = ix + ((v.seed * 3 + Math.floor(v.minutes / 20)) % iw);
    bm.rect(cx, iy + 18, 8, 2, COLORS.paper);
    bm.rect(cx + 2, iy + 17, 4, 1, COLORS.paper);
  }
  // Rooftops of the city.
  bm.rect(ix, iy + ih - 5, iw, 5, night ? COLORS.ink : COLORS.dusk);
  bm.rect(ix + 4, iy + ih - 9, 6, 4, night ? COLORS.ink : COLORS.dusk);
  bm.rect(ix + 20, iy + ih - 11, 5, 6, night ? COLORS.ink : COLORS.dusk);
  if (night) {
    bm.set(ix + 6, iy + ih - 7, COLORS.yellow);
    bm.set(ix + 22, iy + ih - 8, COLORS.yellow);
    bm.set(ix + 13, iy + ih - 3, COLORS.yellow);
  }
  // Mullions and sill.
  bm.rect(x + w / 2 - 1, y, 2, h, COLORS.woodLight);
  bm.rect(x, y + h / 2 - 1, w, 2, COLORS.woodLight);
  bm.rect(x - 3, y + h, w + 6, 2, COLORS.woodLight);
  bm.rect(x - 3, y + h + 2, w + 6, 1, COLORS.ink);
}

function drawDoor(bm: Bitmap, v: RoomView) {
  const x = 8;
  const y = 40;
  const w = 22;
  const h = FLOOR_Y - y;
  bm.rect(x - 2, y - 2, w + 4, h + 2, COLORS.ink);
  bm.rect(x - 1, y - 1, w + 2, h + 1, COLORS.woodLight);
  if (v.doorOpen) {
    bm.rect(x, y, w, h, COLORS.night);
    bm.rect(x, y + h - 6, w, 6, COLORS.ink);
    bm.rect(x, y, 4, h, COLORS.floor);
    bm.rect(x + 3, y, 1, h, COLORS.floorDark);
  } else {
    bm.rect(x, y, w, h, COLORS.floor);
    bm.rect(x + 3, y + 4, w - 6, 18, COLORS.floorDark);
    bm.rect(x + 4, y + 5, w - 8, 16, COLORS.floor);
    bm.rect(x + 3, y + 27, w - 6, 20, COLORS.floorDark);
    bm.rect(x + 4, y + 28, w - 8, 18, COLORS.floor);
    bm.rect(x + w - 4, y + 25, 2, 2, COLORS.yellow);
  }
  if (v.awayNote && !v.doorOpen) {
    bm.rect(x + 6, y + 9, 10, 8, COLORS.ink);
    bm.rect(x + 7, y + 10, 8, 6, COLORS.paper);
    bm.rect(x + 8, y + 12, 6, 1, COLORS.greyDark);
    bm.rect(x + 8, y + 14, 4, 1, COLORS.greyDark);
  }
}

function drawFridge(bm: Bitmap, v: RoomView) {
  const x = 166;
  const y = 44;
  const w = 22;
  const h = FLOOR_Y - y;
  bm.rect(x - 1, y - 1, w + 2, h + 1, COLORS.ink);
  bm.rect(x, y, w, h, COLORS.cream);
  bm.rect(x + w - 3, y, 3, h, COLORS.greyLight);
  bm.rect(x, y + 15, w, 1, COLORS.ink);
  bm.rect(x + 2, y + 5, 1, 7, COLORS.greyDark);
  bm.rect(x + 2, y + 19, 1, 10, COLORS.greyDark);
  bm.rect(x + 2, h + y - 3, w - 4, 3, COLORS.greyLight);
  // Shopping note with one line per item.
  const nx = x + 6;
  const ny = y + 20;
  bm.rect(nx - 1, ny - 1, 11, 14, COLORS.ink);
  bm.rect(nx, ny, 9, 12, COLORS.paper);
  bm.rect(nx + 4, ny - 2, 2, 2, COLORS.red);
  const lines = Math.min(5, v.shopping);
  for (let i = 0; i < lines; i++) bm.rect(nx + 1, ny + 2 + i * 2, 4 + ((i * 3) % 4), 1, COLORS.blueDark);
  // Camping hot plate on top.
  bm.rect(x + 3, y - 4, 12, 3, COLORS.ink);
  bm.rect(x + 4, y - 3, 10, 1, COLORS.greyDark);
  bm.rect(x + 6, y - 4, 4, 1, COLORS.redDark);
}

function drawBulb(bm: Bitmap, on: boolean) {
  const x = 124;
  bm.rect(x, 0, 1, 9, COLORS.ink);
  bm.rect(x - 1, 9, 3, 1, COLORS.greyDark);
  bm.rect(x - 2, 10, 5, 4, COLORS.ink);
  bm.rect(x - 1, 10, 3, 3, on ? COLORS.yellowLight : COLORS.greyLight);
  if (on) {
    bm.set(x - 4, 11, COLORS.yellow);
    bm.set(x + 4, 11, COLORS.yellow);
    bm.set(x, 16, COLORS.yellow);
  }
}

function drawBooks(bm: Bitmap, mindLevel: number) {
  const cols = [
    COLORS.red,
    COLORS.blue,
    COLORS.green,
    COLORS.yellow,
    COLORS.purple,
    COLORS.teal,
    COLORS.orange,
  ];
  // Two books per Mind level, up to four shelves of five.
  const n = Math.min(20, Math.max(2, mindLevel * 2));
  for (let i = 0; i < n; i++) {
    const shelf = Math.floor(i / 5);
    const slot = i % 5;
    const bx = 147 + slot * 2;
    const by = 54 + shelf * 10 + (slot % 2);
    bm.rect(bx, by, 2, 9 - (slot % 2), cols[i % cols.length]!);
    bm.rect(bx + 1, by, 1, 9 - (slot % 2), COLORS.ink);
  }
}

const BAG = gridSprite([".k..", "kGGk", "kGGGk", "kGGGk", "kGGGk", ".kkk."], "top-left");

function drawMess(bm: Bitmap, level: number) {
  if (level >= 1) bm.blit(BAG, 33, 88);
  if (level >= 2) bm.blit(BAG, 38, 88);
  if (level >= 3) {
    bm.rect(36, 97, 14, 2, COLORS.greenDark);
    bm.rect(38, 99, 9, 1, COLORS.greenDark);
  }
}

/** Animated flies above bags (mess level 2+), positions per frame. */
export function flyPositions(level: number, t: number): [number, number][] {
  if (level < 2) return [];
  const n = level >= 3 ? 3 : 2;
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = t / 300 + i * 2.1;
    out.push([Math.round(39 + Math.cos(a) * 5), Math.round(83 + Math.sin(a * 1.7) * 3)]);
  }
  return out;
}
